/**
 * Calendar Integration Hook
 *
 * Hook for managing calendar integration in email, voice and message agents.
 * The agent types share the flow and response shape; only the endpoint differs:
 *   - email: /api/agents/email/calendar-config?agent_id=… (junction-resolved inbox)
 *   - voice: /api/agents/voice/[id]/calendar-config (explicit inbox_id in config)
 *   - messaging: /api/agents/messaging/[id]/calendar-config (explicit inbox_id, voice model)
 */

import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import type { CalendarBookingConfig } from "@/lib/calendar";
import { openOAuthPopup } from "@/lib/oauth/popup";
import { useTranslations } from "next-intl"


/** Voice + message agents carry the explicit credential inbox in the config. */
export type CalendarConfigUpdate = Partial<CalendarBookingConfig> & {
  inbox_id?: string | null;
};

export interface CalendarStatus {
  connected: boolean;
  email: string | null;
}

export interface InboxInfo {
  id: string;
  email: string;
  hasCalendarScope: boolean;
}

export interface UseCalendarIntegrationOptions {
  agentId: string;
  /** Which agent type's calendar-config endpoint to talk to. Default: email. */
  agentType?: "email" | "voice" | "messaging";
  autoLoad?: boolean;
}

export interface UseCalendarIntegrationReturn {
  // State
  config: CalendarBookingConfig | null;
  calendarStatus: CalendarStatus;
  inboxId: string | null;
  inboxes: InboxInfo[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Actions
  loadConfig: () => Promise<void>;
  updateConfig: (config: CalendarConfigUpdate) => Promise<void>;
  connectCalendar: (inboxId: string) => Promise<void>;
  disconnectCalendar: () => Promise<void>;
}

export function useCalendarIntegration(
  options: UseCalendarIntegrationOptions
): UseCalendarIntegrationReturn {
  const tUi = useTranslations("Feedback.UseCalendarIntegration")

  const { agentId, agentType = "email", autoLoad = true } = options;
  const { fetchWithAuth } = useAuth();

  const [config, setConfig] = useState<CalendarBookingConfig | null>(null);
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus>({
    connected: false,
    email: null,
  });
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [inboxes, setInboxes] = useState<InboxInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load calendar config and status
   */
  const loadConfig = useCallback(async () => {
    if (!agentId) return;

    setIsLoading(true);
    setError(null);

    try {
      // email is junction-resolved (query param); voice + messaging are
      // path-based with an explicit inbox in the config.
      const url =
        agentType === "email"
          ? `/api/agents/email/calendar-config?agent_id=${agentId}`
          : `/api/agents/${agentType}/${agentId}/calendar-config`;
      const res = await fetchWithAuth(url);

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to load calendar configuration");
      }

      const data = await res.json();
      setConfig(data.config);
      setCalendarStatus(data.calendar_status || { connected: false, email: null });
      setInboxId(data.inbox_id || null);
      setInboxes(data.inboxes || []);
    } catch (err: unknown) {
      console.error("Failed to load calendar config:", err);
      setError(err instanceof Error && err.message ? err.message : "Failed to load calendar configuration");
    } finally {
      setIsLoading(false);
    }
  }, [agentId, agentType, fetchWithAuth]);

  /**
   * Update calendar config
   */
  const updateConfig = useCallback(
    async (updates: CalendarConfigUpdate) => {
      if (!agentId) return;

      setIsSaving(true);
      setError(null);

      try {
        const res =
          agentType === "email"
            ? await fetchWithAuth("/api/agents/email/calendar-config", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  agent_id: agentId,
                  config: updates,
                }),
              })
            : await fetchWithAuth(`/api/agents/${agentType}/${agentId}/calendar-config`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ config: updates }),
              });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to save calendar configuration");
        }

        const data = await res.json();
        setConfig(data.config);
        if (agentType !== "email" && "inbox_id" in data) {
          setInboxId(data.inbox_id || null);
        }
      } catch (err: unknown) {
        console.error("Failed to update calendar config:", err);
        setError(err instanceof Error && err.message ? err.message : "Failed to save calendar configuration");
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [agentId, agentType, fetchWithAuth]
  );

  /**
   * Initiate calendar OAuth connection for a specific inbox
   */
  const connectCalendar = useCallback(
    async (targetInboxId: string) => {
      if (!targetInboxId) {
        setError(tUi("noInboxSpecifiedPleaseSelectAnInboxTo"));
        return;
      }

      setError(null);

      try {
        const res = await fetchWithAuth("/api/integrations/google/oauth/scope", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            inbox_id: targetInboxId,
            scope_type: "calendar",
          }),
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || "Failed to initiate calendar connection");
        }

        const data = await res.json();

        // Open OAuth popup
        if (data.auth_url) {
          openOAuthPopup({
            url: data.auth_url,
            name: "calendar_oauth",
            onMessage: () => loadConfig(),
            onClose: () => loadConfig(),
            errorKey: "calendar_oauth_error",
            onError: (err) => setError(`Calendar connection failed: ${err}`),
          });
        }
      } catch (err: unknown) {
        console.error("Failed to connect calendar:", err);
        setError(err instanceof Error && err.message ? err.message : "Failed to connect calendar");
      }
    },
    [fetchWithAuth, loadConfig]
  );

  /**
   * Disconnect calendar (disable calendar_enabled flag)
   */
  const disconnectCalendar = useCallback(async () => {
    if (!inboxId) return;

    setIsSaving(true);
    setError(null);

    try {
      // For now, just disable calendar in config
      // Future: Could add dedicated disconnect endpoint
      await updateConfig({ enabled: false });
      setCalendarStatus({ connected: false, email: calendarStatus.email });
    } catch (err: unknown) {
      console.error("Failed to disconnect calendar:", err);
      setError(err instanceof Error && err.message ? err.message : "Failed to disconnect calendar");
    } finally {
      setIsSaving(false);
    }
  }, [inboxId, updateConfig, calendarStatus.email]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad && agentId) {
      loadConfig();
    }
  }, [autoLoad, agentId, loadConfig]);

  return {
    config,
    calendarStatus,
    inboxId,
    inboxes,
    isLoading,
    isSaving,
    error,
    loadConfig,
    updateConfig,
    connectCalendar,
    disconnectCalendar,
  };
}
